// The player's lobby character — a proper humanoid built from primitives.
// Includes head/neck/torso/arms/hands/waist/legs/feet + tactical outfit +
// held weapon. Uses idle animations (breathing, look-around, weapon check).
//
// Palette + optional rigged glTF model come from the active agent
// (see /game/agents/agents.js). Set `agent.modelUrl` to load a real model
// and skip the primitive rig entirely.
import { useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { ContactShadows, useGLTF } from "@react-three/drei";
import * as THREE from "three";
import { useMatchStore } from "@/stores/matchStore";
import { getWeapon } from "@/game/weapons/weapons";
import { useSettings } from "@/stores/settingsStore";
import { getAgent } from "@/game/agents/agents";

// Colours are supplied dynamically by the active agent's palette.
// The DEFAULT_PALETTE is only used as a safety fallback.
const DEFAULT_PALETTE = {
    skin: "#c7a483",
    hair: "#241a12",
    shirt: "#2f3541",
    shirtDark: "#1e232c",
    trousers: "#1a1a1e",
    boots: "#0e0e10",
    gloves: "#111318",
    vest: "#2a2823",
    beltAccent: "#a8621a",
    metal: "#3a3a40",
};

let PALETTE = DEFAULT_PALETTE;

function Head({ lookX = 0, lookY = 0 }) {
    return (
        <group position={[0, 1.68, 0]} rotation={[lookY, lookX, 0]}>
            {/* Head */}
            <mesh castShadow>
                <sphereGeometry args={[0.12, 20, 20]} />
                <meshStandardMaterial color={PALETTE.skin} roughness={0.6} metalness={0.05} />
            </mesh>
            {/* Hair cap */}
            <mesh position={[0, 0.05, -0.005]} castShadow>
                <sphereGeometry args={[0.126, 16, 12, 0, Math.PI * 2, 0, Math.PI / 2]} />
                <meshStandardMaterial color={PALETTE.hair} roughness={0.9} />
            </mesh>
            {/* Ears */}
            <mesh position={[-0.115, 0.005, 0]} castShadow>
                <sphereGeometry args={[0.028, 8, 8]} />
                <meshStandardMaterial color={PALETTE.skin} roughness={0.6} />
            </mesh>
            <mesh position={[0.115, 0.005, 0]} castShadow>
                <sphereGeometry args={[0.028, 8, 8]} />
                <meshStandardMaterial color={PALETTE.skin} roughness={0.6} />
            </mesh>
            {/* Eyebrow line */}
            <mesh position={[0, 0.02, 0.11]}>
                <boxGeometry args={[0.09, 0.008, 0.01]} />
                <meshStandardMaterial color={PALETTE.hair} />
            </mesh>
            {/* Eyes */}
            <mesh position={[-0.035, -0.005, 0.11]}>
                <sphereGeometry args={[0.014, 8, 8]} />
                <meshStandardMaterial color="#0a0a0a" />
            </mesh>
            <mesh position={[0.035, -0.005, 0.11]}>
                <sphereGeometry args={[0.014, 8, 8]} />
                <meshStandardMaterial color="#0a0a0a" />
            </mesh>
            {/* Nose */}
            <mesh position={[0, -0.02, 0.12]}>
                <boxGeometry args={[0.018, 0.03, 0.02]} />
                <meshStandardMaterial color={PALETTE.skin} roughness={0.6} />
            </mesh>
            {/* Jaw shadow line */}
            <mesh position={[0, -0.075, 0.02]}>
                <boxGeometry args={[0.16, 0.02, 0.16]} />
                <meshStandardMaterial color={PALETTE.skin} roughness={0.6} />
            </mesh>
        </group>
    );
}

function Torso() {
    return (
        <group position={[0, 1.28, 0]}>
            {/* Chest */}
            <mesh castShadow>
                <boxGeometry args={[0.44, 0.55, 0.24]} />
                <meshStandardMaterial color={PALETTE.shirt} roughness={0.75} metalness={0.15} />
            </mesh>
            {/* Shoulder pads */}
            <mesh position={[-0.24, 0.2, 0]} castShadow>
                <boxGeometry args={[0.12, 0.14, 0.24]} />
                <meshStandardMaterial color={PALETTE.shirtDark} roughness={0.7} metalness={0.2} />
            </mesh>
            <mesh position={[0.24, 0.2, 0]} castShadow>
                <boxGeometry args={[0.12, 0.14, 0.24]} />
                <meshStandardMaterial color={PALETTE.shirtDark} roughness={0.7} metalness={0.2} />
            </mesh>
            {/* Vest (open plate carrier) */}
            <mesh position={[0, 0.04, 0.13]} castShadow>
                <boxGeometry args={[0.38, 0.42, 0.05]} />
                <meshStandardMaterial color={PALETTE.vest} roughness={0.8} metalness={0.2} />
            </mesh>
            {/* Chest strap accent */}
            <mesh position={[0, 0.02, 0.16]}>
                <boxGeometry args={[0.4, 0.03, 0.01]} />
                <meshStandardMaterial color={PALETTE.beltAccent} roughness={0.5} />
            </mesh>
            {/* Vest pouches */}
            <mesh position={[-0.1, -0.04, 0.16]} castShadow>
                <boxGeometry args={[0.12, 0.1, 0.05]} />
                <meshStandardMaterial color={PALETTE.shirtDark} roughness={0.7} />
            </mesh>
            <mesh position={[0.1, -0.04, 0.16]} castShadow>
                <boxGeometry args={[0.12, 0.1, 0.05]} />
                <meshStandardMaterial color={PALETTE.shirtDark} roughness={0.7} />
            </mesh>
            {/* Neck */}
            <mesh position={[0, 0.32, 0]} castShadow>
                <cylinderGeometry args={[0.06, 0.07, 0.12, 12]} />
                <meshStandardMaterial color={PALETTE.skin} roughness={0.6} />
            </mesh>
        </group>
    );
}

function Waist() {
    return (
        <group position={[0, 0.95, 0]}>
            {/* Belt */}
            <mesh castShadow>
                <boxGeometry args={[0.42, 0.1, 0.24]} />
                <meshStandardMaterial color={PALETTE.trousers} roughness={0.8} metalness={0.2} />
            </mesh>
            {/* Buckle */}
            <mesh position={[0, 0, 0.125]}>
                <boxGeometry args={[0.09, 0.06, 0.02]} />
                <meshStandardMaterial color={PALETTE.metal} roughness={0.4} metalness={0.7} />
            </mesh>
            {/* Holster on right hip */}
            <mesh position={[0.22, -0.06, 0.08]} castShadow>
                <boxGeometry args={[0.05, 0.14, 0.09]} />
                <meshStandardMaterial color={PALETTE.shirtDark} roughness={0.75} />
            </mesh>
        </group>
    );
}

/**
 * Arm: uses two joint groups so upper/forearm/hand animate correctly.
 * dir: 1 = right arm, -1 = left arm
 */
function Arm({ dir = 1, upperRot = [0, 0, 0], forearmRot = [0, 0, 0] }) {
    return (
        <group position={[0.24 * dir, 1.48, 0]} rotation={upperRot}>
            {/* Upper arm */}
            <mesh position={[0, -0.12, 0]} castShadow>
                <capsuleGeometry args={[0.05, 0.18, 4, 8]} />
                <meshStandardMaterial color={PALETTE.shirt} roughness={0.7} />
            </mesh>
            <group position={[0, -0.26, 0]} rotation={forearmRot}>
                {/* Forearm */}
                <mesh position={[0, -0.12, 0]} castShadow>
                    <capsuleGeometry args={[0.045, 0.18, 4, 8]} />
                    <meshStandardMaterial color={PALETTE.shirt} roughness={0.7} />
                </mesh>
                {/* Glove/hand */}
                <mesh position={[0, -0.28, 0]} castShadow>
                    <boxGeometry args={[0.08, 0.12, 0.06]} />
                    <meshStandardMaterial color={PALETTE.gloves} roughness={0.75} />
                </mesh>
            </group>
        </group>
    );
}

function Leg({ dir = 1, kneeRot = 0 }) {
    return (
        <group position={[0.09 * dir, 0.9, 0]}>
            {/* Thigh */}
            <mesh position={[0, -0.22, 0]} castShadow>
                <capsuleGeometry args={[0.075, 0.28, 4, 8]} />
                <meshStandardMaterial color={PALETTE.trousers} roughness={0.85} />
            </mesh>
            <group position={[0, -0.44, 0]} rotation={[kneeRot, 0, 0]}>
                {/* Shin */}
                <mesh position={[0, -0.22, 0]} castShadow>
                    <capsuleGeometry args={[0.065, 0.28, 4, 8]} />
                    <meshStandardMaterial color={PALETTE.trousers} roughness={0.85} />
                </mesh>
                {/* Boot */}
                <mesh position={[0, -0.42, 0.03]} castShadow>
                    <boxGeometry args={[0.12, 0.09, 0.24]} />
                    <meshStandardMaterial color={PALETTE.boots} roughness={0.65} metalness={0.15} />
                </mesh>
                {/* Boot sole accent */}
                <mesh position={[0, -0.475, 0.03]}>
                    <boxGeometry args={[0.13, 0.02, 0.25]} />
                    <meshStandardMaterial color="#1a1a1a" roughness={0.9} />
                </mesh>
            </group>
        </group>
    );
}

function HeldWeapon({ weaponId }) {
    const w = getWeapon(weaponId);
    // Positioned to look like it's being held across the chest at low ready
    return (
        <group position={[0.02, 1.05, 0.28]} rotation={[0, -0.18, 0]}>
            {/* Body */}
            <mesh castShadow>
                <boxGeometry args={[w.model.bodySize[0] * 1.6, w.model.bodySize[1] * 1.6, w.model.bodySize[2] * 1.6]} />
                <meshStandardMaterial color={w.model.color} metalness={0.7} roughness={0.4} />
            </mesh>
            {/* Barrel */}
            <mesh position={[0, 0.02, -(w.model.bodySize[2] + w.model.barrelLen) * 0.8]} castShadow>
                <cylinderGeometry args={[0.02, 0.02, w.model.barrelLen * 1.6, 8]} rotation={[Math.PI / 2, 0, 0]} />
                <meshStandardMaterial color={w.model.barrelColor} metalness={0.9} roughness={0.3} />
            </mesh>
            {/* Sight */}
            <mesh position={[0, 0.09, -w.model.bodySize[2] * 0.4]} castShadow>
                <boxGeometry args={[0.05, 0.08, 0.14]} />
                <meshStandardMaterial
                    color={w.model.sightColor}
                    emissive={w.model.sightColor}
                    emissiveIntensity={0.4}
                    metalness={0.6}
                    roughness={0.3}
                />
            </mesh>
            {/* Magazine */}
            <mesh position={[0, -0.13, -w.model.bodySize[2] * 0.2]} castShadow>
                <boxGeometry args={[0.055, 0.15, 0.09]} />
                <meshStandardMaterial color="#0a0d12" metalness={0.5} roughness={0.5} />
            </mesh>
            {/* Grip */}
            <mesh position={[0, -0.12, w.model.bodySize[2] * 0.3]} rotation={[0.2, 0, 0]} castShadow>
                <boxGeometry args={[0.05, 0.16, 0.08]} />
                <meshStandardMaterial color={w.model.gripColor} roughness={0.7} />
            </mesh>
        </group>
    );
}

function Character({ weaponId }) {
    const rootRef = useRef();
    const state = useRef({
        // Persistent idle animation state
        breathePhase: 0,
        lookX: 0,
        lookY: 0,
        lookTargetX: 0,
        lookTargetY: 0,
        nextLookAt: 0,
        inspectPhase: 0,      // 0 = idle, 1 = raising, 2 = holding, 3 = lowering
        inspectStartedAt: 0,
        nextInspectAt: 0,
    });

    useFrame((_, dt) => {
        const s = state.current;
        const t = performance.now() / 1000;

        // Breathing
        s.breathePhase += dt * 1.4;
        const breath = Math.sin(s.breathePhase) * 0.008;

        // Head look-around (retarget every 4-7 seconds)
        if (t > s.nextLookAt) {
            s.lookTargetX = (Math.random() - 0.5) * 0.7;
            s.lookTargetY = (Math.random() - 0.5) * 0.28;
            s.nextLookAt = t + 3.5 + Math.random() * 3;
        }
        s.lookX += (s.lookTargetX - s.lookX) * Math.min(1, dt * 1.4);
        s.lookY += (s.lookTargetY - s.lookY) * Math.min(1, dt * 1.4);

        // Weapon inspect (every ~14 seconds)
        if (s.inspectPhase === 0 && t > s.nextInspectAt) {
            s.inspectPhase = 1;
            s.inspectStartedAt = t;
        }
        if (s.inspectPhase > 0) {
            const el = t - s.inspectStartedAt;
            if (el > 2.6) {
                s.inspectPhase = 0;
                s.nextInspectAt = t + 10 + Math.random() * 8;
            }
        }

        if (rootRef.current) {
            rootRef.current.position.y = breath;
            // Subtle posture shift
            rootRef.current.rotation.y = Math.sin(t * 0.35) * 0.03;
        }

        // Store computed values for render (via ref props to child nodes)
        rootRef.current.userData.lookX = s.lookX;
        rootRef.current.userData.lookY = s.lookY;
        rootRef.current.userData.inspect = s.inspectPhase > 0
            ? Math.sin(Math.min(1, (t - s.inspectStartedAt) / 2.6) * Math.PI)
            : 0;
    });

    return (
        <AnimatedRig rootRef={rootRef} weaponId={weaponId} />
    );
}

function AnimatedRig({ rootRef, weaponId }) {
    // We piggyback on rootRef.userData for animation values updated in useFrame
    // above. Head + arms read from those on every render (frame).
    const headRef = useRef();
    const leftArmRef = useRef();
    const rightArmRef = useRef();
    const weaponRef = useRef();

    useFrame(() => {
        const ud = rootRef.current?.userData || {};
        const inspect = ud.inspect || 0;
        if (headRef.current) {
            headRef.current.rotation.y = (ud.lookX || 0) + inspect * 0.15;
            headRef.current.rotation.x = (ud.lookY || 0) - inspect * 0.15;
        }
        // Right arm forearm angle raises during inspect
        if (rightArmRef.current) {
            rightArmRef.current.rotation.x = 0.15 - inspect * 0.7;
        }
        if (leftArmRef.current) {
            leftArmRef.current.rotation.x = 0.1 - inspect * 0.4;
        }
        if (weaponRef.current) {
            // Raise weapon toward face during inspect
            weaponRef.current.position.y = 1.05 + inspect * 0.25;
            weaponRef.current.position.z = 0.28 - inspect * 0.1;
            weaponRef.current.rotation.x = -inspect * 0.35;
        }
    });

    return (
        <group ref={rootRef}>
            {/* Head with tracking */}
            <group ref={headRef}>
                <Head />
            </group>
            <Torso />
            <Waist />
            {/* Arms — both slightly angled inward to hold weapon */}
            <group ref={leftArmRef}>
                <Arm dir={-1} upperRot={[0.15, -0.05, -0.35]} forearmRot={[0.6, 0, 0.15]} />
            </group>
            <group ref={rightArmRef}>
                <Arm dir={1} upperRot={[0.2, 0.05, 0.35]} forearmRot={[0.5, 0, -0.2]} />
            </group>
            {/* Legs — stable stance, feet slightly apart */}
            <Leg dir={-1} kneeRot={0.05} />
            <Leg dir={1} kneeRot={-0.05} />
            {/* Weapon */}
            <group ref={weaponRef} position={[0.02, 1.05, 0.28]} rotation={[0, -0.18, 0]}>
                <HeldWeapon weaponId={weaponId} />
            </group>
        </group>
    );
}

function CharacterCameraFraming() {
    // Static: camera at chest height, slight downward tilt, looks perfect on
    // a transparent overlay canvas.
    useFrame((state) => {
        const t = state.clock.getElapsedTime();
        // very small hover
        state.camera.position.set(0, 1.15, 2.15);
        state.camera.position.x += Math.sin(t * 0.3) * 0.02;
        state.camera.position.y += Math.sin(t * 0.6) * 0.01;
        state.camera.lookAt(0, 1.05, 0);
    });
    return null;
}

function CharacterLighting() {
    return (
        <>
            <ambientLight intensity={0.55} color="#f2ddb0" />
            <directionalLight
                position={[-4, 5, 4]}
                intensity={1.1}
                color="#ffe0b0"
                castShadow
                shadow-mapSize-width={1024}
                shadow-mapSize-height={1024}
            />
            <directionalLight position={[4, 4, -2]} intensity={0.35} color="#8faed8" />
            <spotLight
                position={[0, 4, 3]}
                angle={0.4}
                penumbra={0.7}
                intensity={0.8}
                color="#ffd8a0"
                distance={8}
            />
        </>
    );
}

export default function LobbyCharacter() {
    // Reads player's active/preferred weapon so the character always holds
    // the one you're going to deploy with.
    const inMatchWeapon = useMatchStore((s) => s.activeWeapon);
    const preferred = useSettings((s) => s.weaponLoadout?.primary);
    const weaponId = inMatchWeapon || preferred || "rifle";
    const agentId = useSettings((s) => s.selectedAgent) || "recruit";
    const agent = getAgent(agentId);
    // Apply agent palette globally to the primitive rig (module-level palette
    // is read on every render by the mesh components below).
    PALETTE = { ...DEFAULT_PALETTE, ...(agent.palette || {}) };

    return (
        <div
            className="pointer-events-none absolute inset-0 flex items-end justify-center"
            style={{ zIndex: 2 }}
        >
            <div style={{ width: "min(520px, 40vw)", height: "72vh" }}>
                <Canvas
                    shadows
                    camera={{ position: [0, 1.15, 2.15], fov: 40 }}
                    gl={{
                        antialias: true,
                        alpha: true,
                        toneMapping: THREE.ACESFilmicToneMapping,
                        toneMappingExposure: 1.05,
                    }}
                    dpr={[1, 1.5]}
                    style={{ background: "transparent" }}
                >
                    <CharacterLighting />
                    {agent.modelUrl ? (
                        <GltfCharacter url={agent.modelUrl} weaponId={weaponId} />
                    ) : (
                        <Character weaponId={weaponId} />
                    )}
                    <ContactShadows
                        position={[0, 0.005, 0]}
                        opacity={0.7}
                        blur={2}
                        scale={4}
                        far={2}
                    />
                    <CharacterCameraFraming />
                </Canvas>
            </div>
        </div>
    );
}

// --- glTF variant ---
// When `agent.modelUrl` is set, we load a rigged model and attach the currently
// selected weapon to a suitable child bone/mesh (falls back to a fixed offset
// if the model has no `Hand.R` bone). The rest of the surrounding animation
// system (breathing, weapon inspect, idle sway) still applies via the wrapper.
function GltfCharacter({ url, weaponId }) {
    const { scene } = useGLTF(url);
    const rootRef = useRef();
    const weaponRef = useRef();

    useFrame((state) => {
        const t = state.clock.getElapsedTime();
        if (!rootRef.current) return;
        // Idle breathing / tiny sway
        rootRef.current.position.y = Math.sin(t * 1.4) * 0.008;
        rootRef.current.rotation.y = Math.sin(t * 0.35) * 0.03;
        // Weapon inspect every ~14s (Math.sin cycle for smoothness)
        const inspect = Math.max(0, Math.sin(t * 0.45 - 1.2));
        if (weaponRef.current) {
            weaponRef.current.position.y = 1.05 + inspect * 0.25;
            weaponRef.current.rotation.x = -inspect * 0.35;
        }
    });

    return (
        <group ref={rootRef}>
            <primitive object={scene} />
            <group ref={weaponRef} position={[0.02, 1.05, 0.28]} rotation={[0, -0.18, 0]}>
                <HeldWeapon weaponId={weaponId} />
            </group>
        </group>
    );
}
